// Libraries
import React from 'react';

// Supermove
import {Icon, DataLoader} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useQuery, useResponsive, useModal, useToast, ToastType, useDrawer} from '@supermove/hooks';
import {CallrailIntegrationModel, UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {Datetime, List} from '@supermove/utils';

// App
import Button from '@shared/design/components/Button';
import TertiaryButton from '@shared/design/components/Button/TertiaryButton';
import Table from '@shared/design/components/Table';
import {
  ColumnDefinitionType,
  ActionType,
} from '@shared/design/components/Table/components/TableBuilder';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import DeleteCallrailIntegrationModal from '@shared/modules/CallrailIntegration/components/DeleteCallrailIntegrationModal';
import SetupCallrailIntegrationModal from '@shared/modules/CallrailIntegration/components/SetupCallrailIntegrationModal';
import UpdateCallrailIntegrationDrawer from '@shared/modules/CallrailIntegration/components/UpdateCallrailIntegrationDrawer';
import CallrailIntegrationMode from '@shared/modules/CallrailIntegration/enums/CallrailIntegrationMode';
import SettingsPage from 'modules/Organization/Settings/components/SettingsPage';

interface ColumnDefinitionProps {
  refetch: () => void;
  viewer: UserModel;
  successToast: ToastType;
}

const getActions = (
  callrailIntegration: CallrailIntegrationModel,
  viewer: UserModel,
  refetch: () => void,
  successToast: ToastType,
): ActionType<CallrailIntegrationModel>[] => {
  const isCreateCallsMode = callrailIntegration.mode === CallrailIntegrationMode.CREATE_CALLS;

  return [
    // Conditionally include the update action if mode is CREATE_CALLS
    ...List.insertIf(isCreateCallsMode, {
      text: 'Manage Integration',
      onPress: ({handleOpen}: {handleOpen?: () => void}) => {
        handleOpen && handleOpen();
      },
      actionHook: {
        hook: useDrawer as (arg: Record<string, any>) => Record<string, any>,
        hookArgument: {name: 'Update CallRail Integration Drawer'},
        renderComponent: ({isOpen, handleClose}: {isOpen: boolean; handleClose: () => void}) => {
          return (
            <UpdateCallrailIntegrationDrawer
              key={String(isOpen)}
              isOpen={isOpen}
              onClose={handleClose}
              callrailIntegration={callrailIntegration}
              organization={viewer.viewingOrganization}
              onSuccess={() => {
                refetch();
                successToast.handleToast({
                  message: 'CallRail integration updated successfully.',
                });
              }}
            />
          );
        },
      },
    }),

    // Always include the delete action
    {
      text: 'Remove Integration',
      color: colors.red.warning,
      onPress: ({handleOpen}: {handleOpen?: () => void}) => {
        handleOpen && handleOpen();
      },
      actionHook: {
        hook: useModal as (arg: Record<string, any>) => Record<string, any>,
        hookArgument: {name: 'Delete CallRail Integration Modal'},
        renderComponent: ({isOpen, handleClose}) => {
          return (
            <DeleteCallrailIntegrationModal
              key={String(isOpen)}
              isOpen={isOpen}
              handleClose={handleClose}
              callrailIntegration={callrailIntegration}
              onSuccess={() => {
                refetch();
                successToast.handleToast({
                  message: 'CallRail integration removed successfully.',
                });
              }}
            />
          );
        },
      },
    },
  ];
};

const getColumnDefinitions = ({
  refetch,
  viewer,
  successToast,
}: ColumnDefinitionProps): ColumnDefinitionType<CallrailIntegrationModel>[] => {
  return [
    {
      flex: 1,
      headerLabel: 'Integration',
      mobileOptions: {
        isInHeader: true,
        rank: 0,
      },
      cellText: () => 'CallRail',
    },
    {
      flex: 1,
      headerLabel: 'Last Updated',
      cellText: (callrailIntegration: CallrailIntegrationModel) =>
        Datetime.convertToDisplayDatetime(callrailIntegration.updatedAt),
      secondary: {
        cellText: (callrailIntegration: CallrailIntegrationModel) =>
          callrailIntegration.updatedBy?.fullName,
      },
    },
    {
      flex: 0.1,
      actions: (callrailIntegration: CallrailIntegrationModel) =>
        getActions(callrailIntegration, viewer, refetch, successToast),
    },
  ];
};

const OrganizationSettingsCallIntegrationsPage = () => {
  const responsive = useResponsive();

  const {loading, data, refetch} = useQuery(OrganizationSettingsCallIntegrationsPage.query, {
    fetchPolicy: 'cache-and-network',
  });

  const setupCallrailIntegrationModal = useModal({
    name: 'Setup CallRail Integration Modal',
    enableTracking: true,
  });

  const successToast = useToast({
    ToastComponent: SuccessToast,
    message: 'CallRail integration created.',
  });

  return (
    <SettingsPage.PageContent
      isResponsive
      title={'Call Integrations'}
      description={'Add and manage your integrations used for importing call and lead data.'}
    >
      <DataLoader loading={loading} data={data} as={null}>
        {({loadedData}) => {
          return (
            <React.Fragment>
              {responsive.desktop ? (
                <Button
                  iconLeft={Icon.Plus}
                  text={'Add Call Integration'}
                  onPress={setupCallrailIntegrationModal.handleOpen}
                  style={{marginBottom: 24, alignSelf: 'flex-start'}}
                />
              ) : (
                <TertiaryButton
                  iconLeft={Icon.Plus}
                  text={'Add'}
                  onPress={setupCallrailIntegrationModal.handleOpen}
                  isResponsive
                  style={{marginBottom: 24}}
                />
              )}
              <Table
                isLoading={loading}
                columnDefinitions={getColumnDefinitions({
                  refetch,
                  viewer: loadedData.viewer,
                  successToast,
                })}
                items={loadedData.viewer.viewingOrganization.callIntegrations || []}
                emptyStateText={
                  'No CallRail integrations to display. Click "Add Call Integration" to get started.'
                }
              />
              <SetupCallrailIntegrationModal
                key={setupCallrailIntegrationModal.key}
                isOpen={setupCallrailIntegrationModal.isOpen}
                handleClose={setupCallrailIntegrationModal.handleClose}
                refetch={refetch}
                viewer={loadedData.viewer}
              />
            </React.Fragment>
          );
        }}
      </DataLoader>
    </SettingsPage.PageContent>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
OrganizationSettingsCallIntegrationsPage.query = gql`
  ${SetupCallrailIntegrationModal.fragment}
  ${UpdateCallrailIntegrationDrawer.fragment}
  ${DeleteCallrailIntegrationModal.fragment}

  query OrganizationSettingsCallIntegrationsPage {
    ${gql.query}
    viewer {
      id
      viewingOrganization {
        id
        callIntegrations {
          id
          mode
          updatedAt
          ...UpdateCallrailIntegrationDrawer
          updatedBy {
            id
            fullName
          }
          ...DeleteCallrailIntegrationModal
        }
      }
      ...SetupCallrailIntegrationModal
    }
  }
`;

export default OrganizationSettingsCallIntegrationsPage;
